// src/pages/Engagements.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoChatbubblesOutline, 
    IoFlashOutline, 
    IoHeartOutline, 
    IoLockClosedOutline, 
    IoPeopleOutline 
} from 'react-icons/io5';
import image1 from '../assets/images/Messages.png';
import image2 from '../assets/images/Messages2.png';
// We can reuse existing assets for placeholders if they fit the context
import image3 from '../assets/images/Generate.png'; // Represents AI replies
import image4 from '../assets/images/Analytics.png'; // Represents sentiment analysis
import heroBg from '../assets/images/engagements-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{
    "@type": "ListItem",
    "position": 1,
    "name": "Home",
    "item": "https://oolook.in/"
  },{
    "@type": "ListItem",
    "position": 2,
    "name": "Engagements",
    "item": "https://oolook.in/engagements"
  }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Community Management Software",
    "provider": {
        "@type": "Organization",
        "name": "Oolook"
    },
    "name": "AI-Powered Social Media Engagement & Unified Inbox",
    "description": "Oolook's Unified Inbox centralizes all your social media conversations. Use AI smart replies to respond faster, manage your community, and build lasting customer loyalty.",
    "url": "https://oolook.in/engagements"
};

// --- Page-Specific Data ---
const engagementsPageData = {
  seo: {
    title: "Social Media Engagement & Unified Inbox Tool | Oolook",
    description: "Never miss a message. Manage all your DMs, comments, and mentions from every platform in one smart inbox. Respond faster with AI and build a loyal community.",
    image: 'https://oolook.in/og/engagements.png', // Ensure this OG image exists
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoChatbubblesOutline,
    title: "Turn Conversations into Conversions",
    subtitle: "Stop juggling notifications. Oolook's Unified Inbox brings all your DMs, comments, and mentions into one place, so you can engage faster and build a stronger community.",
    backgroundImage: heroBg
  },
  featureSection: {
    title: "The End of Missed Opportunities",
    subtitle: "Every interaction is a chance to build a relationship. Our smart inbox ensures you're always part of the conversation.",
    features: [
      { title: "One Inbox, All Platforms", description: "Combine messages, comments, and mentions from Instagram, X, Facebook, and more into a single, manageable feed.", image: image1 },
      { title: "AI-Powered Smart Replies", description: "Save time with AI-generated responses that learn your brand's voice, allowing you to answer common questions in one click.", image: image3 },
      { title: "Collaborate as a Team", description: "Assign conversations to team members, leave internal notes, and ensure a seamless customer experience, every time.", image: image2 },
      { title: "Understand Audience Sentiment", description: "Our AI analyzes conversations to identify sentiment, helping you prioritize urgent issues and celebrate your biggest fans.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Power of Authentic Engagement",
    benefits: [
      { icon: IoFlashOutline, title: "Respond Instantly", description: "Dramatically cut your response times and increase customer satisfaction with a streamlined workflow." },
      { icon: IoPeopleOutline, title: "Build a Loyal Community", description: "Foster genuine connections by engaging consistently and personally with your audience." },
      { icon: IoLockClosedOutline, title: "Never Miss a Message", description: "A centralized inbox means no more lost DMs or buried comments. Every voice is heard." },
      { icon: IoHeartOutline, title: "Increase Brand Loyalty", description: "Exceptional engagement turns followers into fans and customers into lifelong advocates for your brand." }
    ]
  },
  stepByStep: {
    title: "Master Your Community Management in Minutes",
    subtitle: "From chaos to clarity, Oolook simplifies every step of the engagement process.",
    steps: [
      { title: "1. Connect Your Profiles", description: "Link your social accounts, and Oolook will instantly sync all your conversations into the Unified Inbox." },
      { title: "2. Set Up Smart Filters", description: "Organize your inbox by platform, message type, or sentiment. Let our AI automatically tag and prioritize conversations for you." },
      { title: "3. Engage with AI Assistance", description: "Use one-click smart replies for common queries and spend your valuable time on high-impact conversations." },
      { title: "4. Track Your Performance", description: "Monitor your team's response times and analyze engagement trends to continuously improve your community management strategy." }
    ]
  },
  targetAudience: {
    title: "A Smarter Inbox For..."
  }
};

const Engagements = () => {
  return (
    <FeaturePageLayout pageData={engagementsPageData} />
  );
};

export default Engagements;